import {
  TIME_RANGE_EXPR,
  WHERE_VALUES_EXPR,
  MULTI_LIKE_VALUES_EXPR,
  DimensionIds,
} from "@dataseat-dsp-client/lib-reporter";
import endpointMaker from "configuration/endpoints";
import { useAccountId, useOrganizationId } from "redux/auth";
import { post } from "services/reporter/fetch/rest";
import {
  COLUMNS,
  CREATIVE_COLUMNS,
  CREATIVE_TABLE_NAME,
  TABLE_NAME,
} from "./constants";
import { ChangeHistoryRow } from "./types";
import {
  parseJSONRows,
  parseTSV,
  processDayparting,
  transformCreativeTSVToRows,
  transformTSVToRows,
} from "./utils";

type IChangeHistoryService = () => {
  fetchHistoryData: (
    startDate: string | null,
    endDate: string | null,
    campaignId?: string,
    lineitemId?: string,
    creativeId?: string,
    fieldPathPatterns?: string[]
  ) => Promise<ChangeHistoryRow[]>;
};

export const useChangeHistoryService: IChangeHistoryService = () => {
  const organizationId = useOrganizationId();
  const accountId = useAccountId();

  const reporterUrl = endpointMaker.reporter.historyReport(
    organizationId,
    accountId
  );

  const fetchCampaignHistory = (
    startDate: string | null,
    endDate: string | null,
    campaignId?: string,
    lineitemId?: string,
    fieldPathPatterns?: string[]
  ): Promise<ChangeHistoryRow[]> => {
    const whereConditions: any[] = [];

    if (startDate && endDate) {
      whereConditions.push(TIME_RANGE_EXPR(startDate, endDate));
    }

    if (lineitemId) {
      whereConditions.push(
        WHERE_VALUES_EXPR(DimensionIds.LINEITEM_ID_COLUMN_ID, [lineitemId])
      );
    } else if (campaignId) {
      whereConditions.push(
        WHERE_VALUES_EXPR(DimensionIds.CAMPAIGN_ID_COLUMN_ID, [campaignId])
      );
    }

    if (fieldPathPatterns?.length) {
      whereConditions.push(
        MULTI_LIKE_VALUES_EXPR("FIELD_PATH", fieldPathPatterns)
      );
    }

    const query = {
      select: COLUMNS,
      from: TABLE_NAME,
      ...(whereConditions.length > 0 ? { where: whereConditions } : {}),
      limit: -1,
      format: "TabSeparated",
    };

    return post({ url: reporterUrl, body: query })
      .then((response) => response.text())
      .then((tsvText) => {
        const parsedData = parseTSV(tsvText);
        const transformedData = transformTSVToRows(parsedData);
        return processDayparting(transformedData);
      });
  };

  const fetchCreativeHistory = (
    startDate: string | null,
    endDate: string | null,
    creativeId?: string,
    fieldPathPatterns?: string[]
  ): Promise<ChangeHistoryRow[]> => {
    const whereConditions: any[] = [];

    if (startDate && endDate) {
      whereConditions.push(TIME_RANGE_EXPR(startDate, endDate));
    }

    if (creativeId) {
      whereConditions.push(
        WHERE_VALUES_EXPR(DimensionIds.CREATIVE_ID_COLUMN_ID, [creativeId])
      );
    }

    if (fieldPathPatterns?.length) {
      whereConditions.push(
        MULTI_LIKE_VALUES_EXPR("FIELD_PATH", fieldPathPatterns)
      );
    }

    const query = {
      select: CREATIVE_COLUMNS,
      from: CREATIVE_TABLE_NAME,
      ...(whereConditions.length > 0 ? { where: whereConditions } : {}),
      limit: -1,
      format: "JSON",
    };

    return post({ url: reporterUrl, body: query })
      .then((response) => response.json())
      .then((payload) =>
        transformCreativeTSVToRows(parseJSONRows(payload, CREATIVE_COLUMNS))
      );
  };

  const fetchHistoryData = (
    startDate: string | null,
    endDate: string | null,
    campaignId?: string,
    lineitemId?: string,
    creativeId?: string,
    fieldPathPatterns?: string[]
  ): Promise<ChangeHistoryRow[]> => {
    if (creativeId) {
      return fetchCreativeHistory(
        startDate,
        endDate,
        creativeId,
        fieldPathPatterns
      );
    }

    if (lineitemId || campaignId) {
      return fetchCampaignHistory(
        startDate,
        endDate,
        campaignId,
        lineitemId,
        fieldPathPatterns
      );
    }

    // Org-wide: query both tables and merge
    return Promise.all([
      fetchCampaignHistory(startDate, endDate),
      fetchCreativeHistory(startDate, endDate),
    ]).then(([campaignRows, creativeRows]) => {
      return [...campaignRows, ...creativeRows].sort(
        (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
      );
    });
  };

  return {
    fetchHistoryData,
  };
};
